import { LitElement, html, css } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { map } from 'lit-html/directives/map.js';
import { ShouldUpdate } from '../../../modules/ShouldUpdate';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import { KatapultDialog } from '../../katapult-elements/katapult-dialog';
import { CreateTagDialog } from './create-tag-dialog';
import { when } from 'lit/directives/when.js';

/** @import { Ref } from 'lit/directives/ref.js' */

export class ModelTagsEditor extends LitElement {
  static properties = {
    modelPath: { type: String },
    _modelTags: { type: Object }
  };

  static styles = css`
    #tagsList {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      border: 1px solid var(--sl-color-neutral-200);
      border-radius: var(--sl-border-radius-large);
    }
    model-item-tag {
      display: block;
      margin: var(--sl-spacing-2x-small) 0;
    }
    .row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--sl-spacing-x-small);
      gap: var(--sl-spacing-x-small);
    }
    .row:not(:last-of-type) {
      border-bottom: 1px solid var(--sl-color-neutral-200);
    }
  `;

  render() {
    const modelTagsArray = Object.entries(this._modelTags ?? {}).map(([_id, tag]) => ({ ...tag, _id }));
    return html`
      <katapult-dialog ${ref(this._dialogRef)} title="Model Tags" icon="label" width="400">
        <div id="tagsList">
          ${map(
            modelTagsArray,
            (tag) => html`
              <div class="row">
                <div style="flex-grow: 1;">
                  <model-item-tag .tag=${tag}></model-item-tag>
                </div>
                <sl-divider></sl-divider>
                <katapult-button
                  icon="delete"
                  iconOnly
                  noBorder
                  size="small"
                  @click=${this.deleteTag.bind(this, tag._id)}
                ></katapult-button>
              </div>
            `
          )}
          ${when(
            modelTagsArray.length === 0,
            () => html`
              <div class="row" style="justify-content: center; color: var(--sl-color-neutral-700);">
                <katapult-icon icon="label_off" size="20"></katapult-icon>
                <span>No Tags</span>
              </div>
            `
          )}
        </div>
        <katapult-button
          slot="buttons"
          icon="new_label"
          color="var(--sl-color-primary)"
          @click=${() => this._createTagDialogRef.value?.open()}
          >New Tag</katapult-button
        >
        <katapult-button slot="buttons" dialog-dismiss>Done</katapult-button>
      </katapult-dialog>
      <create-tag-dialog ${ref(this._createTagDialogRef)} .modelPath=${this.modelPath}></create-tag-dialog>
    `;
  }

  constructor() {
    super();
    /** @type {?string} */
    this.modelPath = null;
    /** @type {Object} */
    this._modelTags = null;
    /** @type {?Function} */
    this._disconnectTagsListener = null;
    /** @type {Ref<KatapultDialog>} */
    this._dialogRef = createRef();
    /** @type {Ref<CreateTagDialog>} */
    this._createTagDialogRef = createRef();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectTagsListener?.();
  }

  async deleteTag(tagId) {
    const tag = this._modelTags[tagId];
    const confirmDialog = KatapultDialog.confirm({
      html: html`
        <div style="text-align: center;">
          <span>Are you sure you want to delete the <model-item-tag .tag=${tag}></model-item-tag> tag?</span>
          <span>It will be removed from any assigned items. This action cannot be undone.</span>
        </div>
      `,
      dialog: { color: 'var(--sl-color-danger)' }
    });
    const confirmed = await confirmDialog.confirmed;
    if (!confirmed) return;

    const modelsRef = globalThis.FirebaseWorker.ref(this.modelPath).child('models');
    const itemTypes = [{ key: 'attributes' }, { key: 'mapping_buttons' }, { key: 'input_models' }, { key: 'routines' }];
    await Promise.all(
      itemTypes.map(async (itemType) => {
        itemType.models = (await modelsRef.child(itemType.key).once('value')).val();
      })
    );

    const modelsUpdate = {};
    modelsUpdate[`tags/${tagId}`] = null;
    itemTypes.forEach((itemType) => {
      for (const itemKey in itemType.models) {
        const { tags } = itemType.models[itemKey] ?? {};
        if (!Array.isArray(tags)) continue;
        if (!tags.includes(tagId)) continue;
        modelsUpdate[`${itemType.key}/${itemKey}/tags`] = tags.filter((x) => x !== tagId);
      }
    });

    await modelsRef.update(modelsUpdate);
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('modelPath')) this.#updateModelTagsListener();
    return ShouldUpdate(this, changedProperties);
  }

  #updateModelTagsListener() {
    this._disconnectTagsListener?.();
    if (!this.modelPath) return;

    const modelTagsRef = globalThis.FirebaseWorker.ref(this.modelPath).child('models/tags');
    const callback = (snapshot) => (this._modelTags = snapshot.val());

    modelTagsRef.on('value', callback);
    this._disconnectTagsListener = () => {
      modelTagsRef.off('value', callback);
      this._modelTags = null;
      this._disconnectTagsListener = null;
    };
  }

  open() {
    this._dialogRef.value?.open();
  }
}
customElements.define('model-tags-editor', ModelTagsEditor);
